import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Loader2, CheckCircle, AlertCircle, Clock, FileText, Brain, Link2, Sparkles } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface ParsingStatusBadgeProps {
  candidateId: string;
  showProgress?: boolean;
}

type ParsingStatus = 'pending' | 'processing' | 'completed' | 'failed' | null;

interface ParsingJob {
  status: ParsingStatus;
  progress_percent: number;
  current_phase: string;
  extraction_stats: {
    experience_count?: number;
    education_count?: number;
    skills_count?: number;
    projects_count?: number;
    text_improved?: boolean;
  } | null;
  error_message: string | null;
}

const phaseLabels: Record<string, { label: string; icon: typeof FileText }> = {
  pending: { label: 'Queued', icon: Clock },
  extracting_text: { label: 'Extracting text...', icon: FileText },
  extracting_personal_info: { label: 'Finding contact info...', icon: FileText },
  extracting_experience: { label: 'Parsing experience...', icon: Brain },
  extracting_education: { label: 'Parsing education...', icon: Brain },
  extracting_skills: { label: 'Identifying skills...', icon: Brain },
  extracting_projects: { label: 'Finding projects...', icon: Brain },
  improving_text: { label: 'Improving text quality...', icon: Sparkles },
  linking_skills: { label: 'Linking skills...', icon: Link2 },
  completed: { label: 'Complete', icon: CheckCircle },
  failed: { label: 'Failed', icon: AlertCircle },
};

const ParsingStatusBadge = ({ candidateId, showProgress = false }: ParsingStatusBadgeProps) => {
  const [parsingJob, setParsingJob] = useState<ParsingJob | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStatus = async () => {
      const { data } = await supabase
        .from('parsing_jobs')
        .select('status, progress_percent, current_phase, extraction_stats, error_message')
        .eq('candidate_id', candidateId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
      
      if (data) {
        setParsingJob({
          status: data.status as ParsingStatus,
          progress_percent: data.progress_percent || 0,
          current_phase: data.current_phase || 'pending',
          extraction_stats: data.extraction_stats as ParsingJob['extraction_stats'],
          error_message: data.error_message,
        });
      }
      setLoading(false);
    };

    fetchStatus();

    const channel = supabase
      .channel(`parsing-status-${candidateId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'parsing_jobs',
          filter: `candidate_id=eq.${candidateId}`
        },
        (payload: any) => {
          if (payload.new) {
            setParsingJob({
              status: payload.new.status as ParsingStatus,
              progress_percent: payload.new.progress_percent || 0,
              current_phase: payload.new.current_phase || 'pending',
              extraction_stats: payload.new.extraction_stats,
              error_message: payload.new.error_message,
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [candidateId]);

  if (loading || !parsingJob) return null;

  const { status, progress_percent, current_phase, extraction_stats, error_message } = parsingJob;

  // Don't show badge if parsing is completed
  if (status === 'completed') return null;

  const phaseInfo = phaseLabels[current_phase] || phaseLabels.pending;
  const PhaseIcon = phaseInfo.icon;

  const getStatusClasses = () => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/20';
      case 'processing':
        return 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20';
      case 'failed':
        return 'bg-destructive/10 text-destructive border-destructive/20';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  if (showProgress && status === 'processing') {
    return (
      <div className="space-y-2 w-full max-w-xs">
        <div className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            {phaseInfo.label}
          </span>
          <span className="text-muted-foreground">{progress_percent}%</span>
        </div>
        <Progress value={progress_percent} className="h-1.5" />
        {extraction_stats && (
          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
            {extraction_stats.experience_count !== undefined && extraction_stats.experience_count > 0 && (
              <span>{extraction_stats.experience_count} experience</span>
            )}
            {extraction_stats.education_count !== undefined && extraction_stats.education_count > 0 && (
              <span>{extraction_stats.education_count} education</span>
            )}
            {extraction_stats.skills_count !== undefined && extraction_stats.skills_count > 0 && (
              <span>{extraction_stats.skills_count} skills</span>
            )}
            {extraction_stats.projects_count !== undefined && extraction_stats.projects_count > 0 && (
              <span>{extraction_stats.projects_count} projects</span>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="outline" className={`gap-1 ${getStatusClasses()}`}>
            {status === 'processing' ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <PhaseIcon className="h-3 w-3" />
            )}
            {status === 'processing' ? `${progress_percent}%` : phaseInfo.label}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-1">
            <p className="font-medium">{phaseInfo.label}</p>
            {error_message && (
              <p className="text-xs text-destructive">{error_message}</p>
            )}
            {extraction_stats && status === 'processing' && (
              <div className="text-xs text-muted-foreground">
                {extraction_stats.experience_count !== undefined && extraction_stats.experience_count > 0 && (
                  <p>✓ {extraction_stats.experience_count} work experiences found</p>
                )}
                {extraction_stats.education_count !== undefined && extraction_stats.education_count > 0 && (
                  <p>✓ {extraction_stats.education_count} education records found</p>
                )}
                {extraction_stats.skills_count !== undefined && extraction_stats.skills_count > 0 && (
                  <p>✓ {extraction_stats.skills_count} skills identified</p>
                )}
                {extraction_stats.projects_count !== undefined && extraction_stats.projects_count > 0 && (
                  <p>✓ {extraction_stats.projects_count} projects found</p>
                )}
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default ParsingStatusBadge;
